import React from 'react';
import { 
  TableRow, 
  TableCell, 
  IconButton,
  Chip,
  Tooltip,
  Box,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ScienceIcon from '@mui/icons-material/Science';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { User } from '../services/UsersService';

// Table Row Component
const UserRow: React.FC<{
  user: User;
  accountId: number;
  handleEdit: (id: number) => void;
  handleAddSolution: (id: number) => void;
  handleAddSubscription: (id: number) => void;
  formatDate: (date: string) => string;
}> = React.memo(({ user, accountId, handleEdit, handleAddSolution, handleAddSubscription, formatDate }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Navigate to user solutions page
  const navigateToSolutionsPage = (e: React.MouseEvent, userId: number) => {
    e.stopPropagation();
    navigate(`/accounts/${accountId}/users/${userId}/solutions`);
  };

  // Navigate to user subscriptions page
  const navigateToSubscriptionsPage = (e: React.MouseEvent, userId: number) => {
    e.stopPropagation();
    navigate(`/accounts/${accountId}/users/${userId}/subscriptions`);
  };
  
  return (
    <TableRow 
      hover 
      sx={{ 
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: `${alpha(theme.palette.primary.light, 0.08)} !important`,
          transform: 'translateY(-1px)'
        }
      }}
    >
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}>
        {user.id}
      </TableCell>
      <TableCell>
        <Typography variant="body1" fontWeight={500} sx={{ color: theme.palette.primary.main }}>
          {`${user.firstName} ${user.lastName}`}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {user.username}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDate(user.updateDate)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {user.enabled ? (
          <Tooltip title="Enabled">
            <Box sx={{ display: 'inline-flex' }}>
              <CheckCircleIcon 
                color="success" 
                sx={{ 
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }} 
              />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Disabled">
            <Box sx={{ display: 'inline-flex' }}>
              <CancelIcon 
                color="error" 
                sx={{ 
                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }} 
              />
            </Box>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {user.roles?.map((role) => (
              <Chip
                key={role.name}
                label={role.displayName}
                size="small"
                sx={{
                  fontWeight: 500,
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.dark,
                  borderRadius: '8px'
                }}
              />
            ))
          }
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Tooltip title="Edit User">
            <IconButton 
              onClick={() => handleEdit(user.id)} 
              color="primary"
              sx={{ 
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  transform: 'scale(1.1)'
                }
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Manage Solutions">
            <IconButton 
              onClick={(e) => navigateToSolutionsPage(e, user.id)}
              color="secondary"
              sx={{ 
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  transform: 'scale(1.1)'
                }
              }}
            >
              <ScienceIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Manage Subscriptions">
            <IconButton 
              onClick={(e) => navigateToSubscriptionsPage(e, user.id)}
              color="info"
              sx={{ 
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  transform: 'scale(1.1)'
                }
              }}
            >
              <SubscriptionsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

export default UserRow;